import { OrbitsGame } from '../../games/orbits/OrbitsGame.js';
import { OrbitsVortex } from '../../games/orbits/OrbitsVortex.js';
import { OrbitsRenderer } from '../../games/orbits/OrbitsRenderer.js';
import { Grid } from '../../core/Grid.js';
import { Position, positionKey } from '../../core/GridGeometry.js';
import { RenderConfig } from '../../rendering/Renderer.js';
import { TutorialGameAdapter } from '../TutorialEngine.js';

// ── Orbits-specific types ──────────────────────────────────────────

export interface OrbitsRenderOptions {
    // Placeholder — OrbitsRenderer is fully game-state driven
}

// ── Adapter ─────────────────────────────────────────────────────

export class OrbitsTutorialAdapter implements TutorialGameAdapter {
    private game: OrbitsGame;
    private renderer: OrbitsRenderer;
    private grid: Grid;

    constructor(canvas: HTMLCanvasElement, grid: Grid) {
        this.grid = grid;
        this.game = new OrbitsGame(grid);
        this.renderer = new OrbitsRenderer(canvas, grid);
        // Static scenes: no slot tween, so the renderer never self-re-renders and
        // wipes the engine-drawn annotations (was blanking text on scenes 5/6/8).
        this.renderer.setSelfAnimation(false);
    }

    applyState(state: unknown): void {
        const raw = state as string;
        this.game.reset();

        if (!raw) return;

        // Parse "stateString+digitPos" notation
        // Left of '+': standard Orbits serialized state (launcher + gates, including oXY rim gates)
        // Right of '+': digit cell position to select (toggle path display)
        const plusIndex = raw.indexOf('+');
        let statePart: string;
        let clickPart: string;

        if (plusIndex >= 0) {
            statePart = raw.substring(0, plusIndex);
            clickPart = raw.substring(plusIndex + 1);
        } else {
            statePart = raw;
            clickPart = '';
        }

        if (statePart) {
            this.game.deserialize(statePart);
        }

        if (clickPart) {
            const trimmed = clickPart.trim();
            if (trimmed) {
                const pos = this.grid.serializer.deserializePosition(trimmed);
                const digit = this.grid.getDigitAt(pos);
                if (digit !== null) {
                    this.game.toggleSelectedDigit(digit);
                }
            }
        }
    }

    render(_options: unknown): void {
        this.renderer.render(this.game);
    }

    getConfig(): RenderConfig {
        return this.renderer.getConfig();
    }
}

export function createOrbitsTutorialAdapter(
    canvas: HTMLCanvasElement,
    grid: Grid,
): TutorialGameAdapter {
    return new OrbitsTutorialAdapter(canvas, grid);
}

// ── Vortex state ─────────────────────────────────────────────────

export interface OrbitsVortexTutorialState {
    orbits: string;
    gateMode?: boolean;
    /**
     * Extra gate pairs (cell strings, e.g. ['B5', 'D5']) merged on top of the
     * adapter's default teleport pair for this scene only — used by the
     * activation scene to show a second, blocked (amber) pair. Omit otherwise.
     */
    extraGates?: Array<[string, string]>;
}

/** Parse a cell string like "B5" into a Position ({ row: 5, col: 1 }). */
function parseCellString(s: string): Position {
    return { col: s.charCodeAt(0) - 65, row: parseInt(s.slice(1), 10) };
}


// ── Vortex Adapter ───────────────────────────────────────────────

export class OrbitsVortexTutorialAdapter implements TutorialGameAdapter {
    private game: OrbitsVortex;
    private renderer: OrbitsRenderer;
    private grid: Grid;
    private gatePairs: Array<[Position, Position]>;
    private allGates: Set<string>;
    private currentGateMode: boolean = false;

    constructor(
        canvas: HTMLCanvasElement,
        grid: Grid,
        gatePairs: Array<[Position, Position]>,
    ) {
        this.grid = grid;
        this.game = new OrbitsVortex(grid);
        this.renderer = new OrbitsRenderer(canvas, grid);
        this.renderer.setSelfAnimation(false); // see base adapter
        this.gatePairs = gatePairs;
        this.allGates = new Set<string>();
        for (const [a, b] of gatePairs) {
            this.allGates.add(positionKey(a));
            this.allGates.add(positionKey(b));
        }
    }

    applyState(state: unknown): void {
        const s = state as OrbitsVortexTutorialState | null;
        this.game.reset();
        const pairs = [...this.gatePairs];
        for (const [a, b] of s?.extraGates ?? []) {
            pairs.push([parseCellString(a), parseCellString(b)]);
        }
        const allGates = new Set<string>();
        for (const [a, b] of pairs) {
            allGates.add(positionKey(a));
            allGates.add(positionKey(b));
        }
        this.game.setVortexData(pairs, allGates);

        if (s?.orbits) {
            // Parse "stateString+digitPos" notation (same as base adapter)
            const plusIndex = s.orbits.indexOf('+');
            let statePart: string;
            let clickPart: string;

            if (plusIndex >= 0) {
                statePart = s.orbits.substring(0, plusIndex);
                clickPart = s.orbits.substring(plusIndex + 1);
            } else {
                statePart = s.orbits;
                clickPart = '';
            }

            if (statePart) {
                this.game.deserialize(statePart);
            }

            if (clickPart) {
                const trimmed = clickPart.trim();
                if (trimmed) {
                    const pos = this.grid.serializer.deserializePosition(trimmed);
                    const digit = this.grid.getDigitAt(pos);
                    if (digit !== null) {
                        this.game.toggleSelectedDigit(digit);
                    }
                }
            }
        }

        this.currentGateMode = s?.gateMode ?? false;
    }

    render(_options: unknown): void {
        this.renderer.setGateMode(this.currentGateMode);
        this.renderer.render(this.game);
    }

    getConfig(): RenderConfig {
        return this.renderer.getConfig();
    }
}

export function createOrbitsVortexTutorialAdapter(
    canvas: HTMLCanvasElement,
    grid: Grid,
): TutorialGameAdapter {
    // Gate pair for the vortex orbits tutorial: B3 ↔ A1
    const gatePairs: Array<[Position, Position]> = [
        [{ row: 3, col: 1 }, { row: 1, col: 0 }],
    ];
    return new OrbitsVortexTutorialAdapter(canvas, grid, gatePairs);
}
